import { CSSProperties, FC, ReactNode } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import type { DocumentTableRow } from './model';
import { getDocumentTableTheme } from './theme';

interface DocumentTableProps {
    blockId?: string;
    focused?: boolean;
    page?: number;
    rows: DocumentTableRow[];
    style?: CSSProperties;
    renderCellText?: (text: string, range: DocumentTableCellTextRange) => ReactNode;
}

export interface DocumentTableCellTextRange {
    columnIndex: number;
    end: number;
    rowIndex: number;
    start: number;
}

const buildCellTextRanges = (rows: DocumentTableRow[]) => {
    let textOffset = 0;

    return rows.map((row, rowIndex) => {
        const ranges = row.map((cell, columnIndex): DocumentTableCellTextRange => {
            const start = textOffset;

            textOffset += cell.text.length;
            const range = { columnIndex, end: textOffset, rowIndex, start };
            if (columnIndex < row.length - 1) {
                textOffset += 1;
            }
            return range;
        });

        if (rowIndex < rows.length - 1) {
            textOffset += 1;
        }
        return ranges;
    });
};

/**
 * Canonical read-only document table presentation. Memo/TipTap owns editing
 * behavior; extracted documents reuse its visual language without exposing
 * mutation controls or weakening source-document fidelity.
 */
const DocumentTable: FC<DocumentTableProps> = ({ blockId, focused, page, renderCellText, rows, style }) => {
    const { token } = useAntDesignTheme();
    const theme = getDocumentTableTheme(token);
    const cellRanges = buildCellTextRanges(rows);

    return (
        <div
            data-ontology-block-id={blockId}
            data-ontology-page={page}
            style={{
                background: theme.background,
                border: `1px solid ${theme.border}`,
                borderRadius: theme.borderRadius,
                boxShadow: focused ? `0 0 0 2px ${theme.selectionBorder}` : undefined,
                margin: '1rem 0',
                maxWidth: '100%',
                overflowX: 'auto',
                scrollMarginBlock: '5rem',
                transition: `box-shadow ${token.motionDurationMid}`,
                ...style,
            }}
        >
            <table
                style={{
                    background: theme.background,
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    minWidth: '30rem',
                    tableLayout: 'auto',
                    width: '100%',
                }}
            >
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={`document-table-row:${rowIndex}`}>
                            {row.map((cell, cellIndex) => {
                                const Cell = cell.header ? 'th' : 'td';

                                return (
                                    <Cell
                                        key={`document-table-cell:${rowIndex}:${cellIndex}`}
                                        colSpan={cell.colspan}
                                        rowSpan={cell.rowspan}
                                        style={{
                                            background: cell.header ? theme.headerBackground : theme.background,
                                            borderBottom:
                                                rowIndex < rows.length - 1 ? `1px solid ${theme.border}` : undefined,
                                            borderRight:
                                                cellIndex < row.length - 1 ? `1px solid ${theme.border}` : undefined,
                                            color: theme.headerText,
                                            fontWeight: cell.header ? token.fontWeightStrong : undefined,
                                            minWidth: '6rem',
                                            padding: '0.5rem 0.625rem',
                                            textAlign: 'left',
                                            verticalAlign: 'top',
                                        }}
                                    >
                                        {renderCellText?.(cell.text, cellRanges[rowIndex][cellIndex]) ?? cell.text}
                                    </Cell>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default DocumentTable;
